import { useEffect, useRef, useState, useMemo } from "react";
import { Spin } from "antd";
import { PDFDocument } from "pdf-lib";
import {
  ActionButton,
  ImageOverlay,
  MirroredImgContainer,
  ModalOverlay,
} from "./style";
import html2canvas from "html2canvas";
import {
  printCredentialTotem,
  confirmCredentialPrintTotem,
  confirmCredentialPrintCancelTotem,
} from "../../../../service/credentials.service";
import { useIntl } from "react-intl";
import useNotification from "../../../../hooks/newUXNotification";
import { printPdfKioskSafe } from "../../../../utils/printPdfKioskSafe";

const CredenticalImagePreview = ({
  fncToReset,
  fncToNotifyByCode,
  credentialData,
  activeToken,
  viewCredentials,
  handleWaitingForPrintAssemble,
  templateCache,
  printConfiguration,
}) => {
  const printRef = useRef();
  const intl = useIntl();
  const notification = useNotification();

  const [credentialImage, setCredentialImage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisibleConfirmPrint, setModalVisibleConfirmPrint] =
    useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const EMPTY_RESOLVED_IMAGE = {
    templateBase64: "",
    credentialBase64: "",
  };

  // Impressão automática direta (modo STAMP)
  const handleDirectPrint = async (credentialOutputs) => {
    setIsPrinting(true);

    try {
      // Confirma a impressão
      const confirmResponse = await confirmCredentialPrintTotem({
        activeToken,
        credentialsId: [credentialData.credentialId],
      });

      const { code, error, updatedCredentialsId } = confirmResponse.data;

      const canPrint =
        confirmResponse.status === 200 &&
        error === false &&
        code === 15011 &&
        Array.isArray(updatedCredentialsId) &&
        updatedCredentialsId.includes(credentialData.credentialId);

      if (!canPrint) {
        notification.displayError(
          intl.formatMessage({
            id: "selfService.error_print_confirm",
          })
        );
        return;
      }

      // Dimensões da etiqueta conforme Jasper Report (257 x 141 pontos)
      const LABEL_WIDTH = 257;
      const LABEL_HEIGHT = 141;

      const pdfDoc = await PDFDocument.create();

      // Processa cada credencial criando uma página de etiqueta para cada
      for (const output of credentialOutputs) {
        const page = pdfDoc.addPage([LABEL_WIDTH, LABEL_HEIGHT]);

        const credentialImageBytes = Uint8Array.from(
          atob(output.credentialBase64),
          (c) => c.charCodeAt(0)
        );

        const credentialImage = await pdfDoc.embedPng(credentialImageBytes);

        // Preenche toda a área da etiqueta com a credencial
        page.drawImage(credentialImage, {
          x: 0,
          y: 0,
          width: LABEL_WIDTH,
          height: LABEL_HEIGHT,
        });
      }

      const pdfBytes = await pdfDoc.save();
      printPdfKioskSafe(pdfBytes);

      const wasNotified = fncToNotifyByCode(32001);
      if (wasNotified) fncToReset();
    } catch (error) {
      notification.displayError(
        intl.formatMessage({
          id: "selfService.error_print_confirm",
        })
      );
    } finally {
      setIsPrinting(false);
      handleWaitingForPrintAssemble(false);
    }
  };

  useEffect(() => {
    if (!credentialData) return;

    const fetchToPrint = async () => {
      setLoading(true);

      try {
        const response = await printCredentialTotem({
          activeToken,
          extension: "png",
          credentialsId: [credentialData.credentialId],
          credentialCompanyName: credentialData.companyName,
        });

        const outputs = response.data.reportOutputList.map((item) => ({
          baseTemplateS3FileKey: item.baseTemplateS3FileKey,
          credentialBase64: item.credentialOutput.file,
        }));

        setCredentialImage(outputs);

        // Se for TEMPLATE, mostra modal de pré-visualização
        // Se não for TEMPLATE (modo STAMP), imprime diretamente
        if (printConfiguration === "TEMPLATE") {
          setModalVisibleConfirmPrint(true);
        } else {
          // Impressão automática sem modal - processa todas as credenciais
          await handleDirectPrint(outputs);
        }
      } catch (error) {
        const wasNotified = fncToNotifyByCode(error?.response?.data?.code);

        if (wasNotified) fncToReset();
        else {
          notification.displayError(
            intl.formatMessage({ id: "selfService.error_print" })
          );
        }

        // Desativa o loading em caso de erro, independente do printConfiguration
        handleWaitingForPrintAssemble(false);
      } finally {
        setLoading(false);
        handleWaitingForPrintAssemble(false);
      }
    };

    fetchToPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialData]);

  useEffect(() => {
    if (!viewCredentials && modalVisibleConfirmPrint) {
      setModalVisibleConfirmPrint(false);
    }
  }, [viewCredentials, modalVisibleConfirmPrint]);

  // ===============================
  // RESOLVE IMAGENS (O(1))
  // ===============================
  const resolvedImages = useMemo(() => {
    if (!credentialImage.length) return EMPTY_RESOLVED_IMAGE;

    const item = credentialImage[0];

    return {
      templateBase64:
        printConfiguration === "TEMPLATE"
          ? templateCache?.[item.baseTemplateS3FileKey] ?? ""
          : "",
      credentialBase64: item.credentialBase64 ?? "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialImage, templateCache, printConfiguration]);

  // ===============================
  // UTIL: ESPERAR IMAGENS CARREGAREM
  // ===============================
  const waitForImages = async (container) => {
    const images = container.querySelectorAll("img");

    await Promise.all(
      Array.from(images).map((img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            })
      )
    );
  };

  const logPrintCancel = async () => {
    try {
      await confirmCredentialPrintCancelTotem({
        activeToken,
        credentialsId: [credentialData.credentialId],
      });
    } catch {
      notification.displayError(intl.formatMessage({ id: "error" }));
    }
  };

  // ===============================
  // CONFIRMAR + IMPRIMIR
  // ===============================
  const handlePrintPDF = async () => {
    if (!printRef.current || isPrinting) return;

    setIsPrinting(true);

    try {
      const confirmResponse = await confirmCredentialPrintTotem({
        activeToken,
        credentialsId: [credentialData.credentialId],
      });

      const { code, error, updatedCredentialsId } = confirmResponse.data;

      const canPrint =
        confirmResponse.status === 200 &&
        error === false &&
        code === 15011 &&
        Array.isArray(updatedCredentialsId) &&
        updatedCredentialsId.includes(credentialData.credentialId);

      if (!canPrint) {
        notification.displayError(
          intl.formatMessage({
            id: "selfService.error_print_confirm",
          })
        );
        return;
      }

      await waitForImages(printRef.current);

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]);

      const pngImageBytes = await fetch(imgData).then((res) =>
        res.arrayBuffer()
      );
      const pngImage = await pdfDoc.embedPng(pngImageBytes);

      const { width, height } = pngImage.scale(1);
      const scale = Math.min(
        page.getWidth() / width,
        page.getHeight() / height
      );

      page.drawImage(pngImage, {
        x: (page.getWidth() - width * scale) / 2,
        y: (page.getHeight() - height * scale) / 2,
        width: width * scale,
        height: height * scale,
      });

      const pdfBytes = await pdfDoc.save();
      printPdfKioskSafe(pdfBytes);

      setModalVisibleConfirmPrint(false);

      const wasNotified = fncToNotifyByCode(32001);
      if (wasNotified) fncToReset();
    } catch {
      notification.displayError(
        intl.formatMessage({
          id: "selfService.error_print_confirm",
        })
      );
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <>
      {/* Modal só aparece no modo TEMPLATE */}
      {printConfiguration === "TEMPLATE" && (
        <ModalOverlay
          open={modalVisibleConfirmPrint}
          closable={false}
          centered
          destroyOnClose
          footer={[
            <ActionButton
              disabled={isPrinting}
              key="close"
              onClick={async () => {
                await logPrintCancel();
                setModalVisibleConfirmPrint(false);
              }}
            >
              {intl.formatMessage({ id: "cancel" })}
            </ActionButton>,
            <ActionButton
              print
              loading={isPrinting}
              key="print"
              type="primary"
              onClick={handlePrintPDF}
            >
              {intl.formatMessage({ id: "confirm" })}
            </ActionButton>,
          ]}
        >
      <Spin spinning={loading}>
        <div
          ref={printRef}
          style={{
            position: "relative",
            width: "100%",
            paddingBottom: "141%",
            backgroundColor: "#fff",
          }}
        >
          {/* Só renderiza template se printConfiguration for "TEMPLATE" */}
          {printConfiguration === "TEMPLATE" && resolvedImages.templateBase64 && (
            <img
              src={`data:image/png;base64,${resolvedImages.templateBase64}`}
              alt="template"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          )}

          <MirroredImgContainer>
            <ImageOverlay>
              <img
                src={`data:image/png;base64,${resolvedImages.credentialBase64}`}
                alt="credential_left"
              />
            </ImageOverlay>
            <ImageOverlay>
              <img
                src={`data:image/png;base64,${resolvedImages.credentialBase64}`}
                alt="credential_right"
              />
            </ImageOverlay>
          </MirroredImgContainer>
        </div>
      </Spin>
        </ModalOverlay>
      )}
    </>
  );
};

export default CredenticalImagePreview;
